import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ChangeEvent,
  type KeyboardEvent,
  type Dispatch,
  type SetStateAction,
} from "react";
import { translate } from "@/i18n";
import { settingsShellMessages } from "@/i18n/messages/settings-shell";

export type SettingsDraftSnapshot = Record<string, unknown>;
const sessions = new Map<string, SettingsDraftSnapshot>();
const retiredSessions = new Set<string>();
const listeners = new Set<() => void>();
export const SettingsDraftSessionContext = createContext<string | null>(null);
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
const notify = () => listeners.forEach((listener) => listener());

/** Drafts live only in memory and travel inside the authenticated frame transfer. */
export function getSettingsDraftSnapshot(
  sessionId: string,
): SettingsDraftSnapshot {
  return structuredClone(sessions.get(sessionId) ?? {});
}
export function hydrateSettingsDraftSnapshot(
  sessionId: string,
  values: SettingsDraftSnapshot,
) {
  if (retiredSessions.has(sessionId)) return;
  sessions.set(sessionId, structuredClone(values));
  notify();
}
export function clearSettingsDraftSession(sessionId: string) {
  retiredSessions.add(sessionId);
  sessions.delete(sessionId);
  notify();
}
export function clearAllSettingsDrafts() {
  for (const sessionId of sessions.keys()) retiredSessions.add(sessionId);
  sessions.clear();
  notify();
}
export function initializeSettingsDraftSession(sessionId: string | null) {
  if (
    sessionId &&
    !sessions.has(sessionId) &&
    !retiredSessions.has(sessionId)
  ) {
    sessions.set(sessionId, {});
  }
}

/** The initializer fills fetched defaults without overwriting transferred edits. */
export function useSettingsDraftState<T>(
  key: string,
  initial: T | (() => T),
): [T, Dispatch<SetStateAction<T>>, Dispatch<SetStateAction<T>>] {
  const sessionId = useContext(SettingsDraftSessionContext);
  const [fallback, initialize] = useState(initial);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const getSnapshot = useCallback(
    () => (sessionId ? sessions.get(sessionId)?.[key] : undefined),
    [key, sessionId],
  );
  const draft = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const value = draft === undefined ? fallback : (draft as T);
  const setValue = useCallback<Dispatch<SetStateAction<T>>>(
    (next) => {
      if (!mounted.current || (sessionId && retiredSessions.has(sessionId)))
        return;
      if (!sessionId) {
        initialize(next);
        return;
      }
      const current = sessions.get(sessionId) ?? {};
      const previous =
        current[key] === undefined ? fallback : (current[key] as T);
      const resolved =
        typeof next === "function" ? (next as (value: T) => T)(previous) : next;
      sessions.set(sessionId, { ...current, [key]: resolved });
      notify();
    },
    [fallback, key, sessionId],
  );
  return [value, setValue, initialize];
}

/** Explicit Cancel discards only that editor; display transfer never calls this. */
export function useResetSettingsDraft() {
  const sessionId = useContext(SettingsDraftSessionContext);
  return useCallback(
    (prefix: string) => {
      if (!sessionId || retiredSessions.has(sessionId)) return;
      const current = sessions.get(sessionId);
      if (!current) return;
      sessions.set(
        sessionId,
        Object.fromEntries(
          Object.entries(current).filter(([key]) => !key.startsWith(prefix)),
        ),
      );
      notify();
    },
    [sessionId],
  );
}

export interface SavedTextField {
  text: string;
  error: string | null;
  saving: boolean;
  /** Waits for a pending save, then commits the latest valid value. */
  saveNow: () => Promise<boolean>;
  inputProps: {
    value: string;
    onChange: (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void;
    onBlur: () => void;
    onKeyDown: (
      event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void;
    "aria-invalid": boolean;
  };
}

function saveErrorMessage(failure: unknown): string {
  const data = (failure as { data?: Record<string, unknown> } | null)?.data;
  const detail = data?.detail ?? data?.error;
  if (typeof detail === "string" && detail.trim()) return detail;
  const listed = data
    ? Object.values(data).find(
        (value): value is string[] =>
          Array.isArray(value) && typeof value[0] === "string",
      )
    : undefined;
  return listed?.[0] ?? translate(settingsShellMessages, "saveFailed");
}

/** Autosave text on blur, Enter and close; serialize writes so late saves cannot overwrite newer edits. */
export function useSavedTextField({
  draftKey,
  saved,
  validate,
  save,
  multiline = false,
}: {
  draftKey: string;
  saved: string;
  validate?: (text: string) => string | null;
  save: (text: string) => Promise<void>;
  multiline?: boolean;
}): SavedTextField {
  const [draft, setDraft] = useSettingsDraftState<string | null>(
    draftKey,
    null,
  );
  const [text, setText] = useState(draft ?? saved);
  const [error, setError] = useState<string | null>(() =>
    draft !== null ? (validate?.(draft) ?? null) : null,
  );
  const [saving, setSaving] = useState(false);
  const mounted = useRef(true);
  const confirmed = useRef(saved);
  const incomingSaved = useRef(saved);
  const inFlight = useRef(false);
  const completed = useRef<Promise<boolean> | null>(null);
  const writingValue = useRef(saved);
  const latest = useRef({ text, validate, save });
  latest.current = { text, validate, save };

  useEffect(() => {
    if (incomingSaved.current === saved) return;
    incomingSaved.current = saved;
    const previous = confirmed.current;
    confirmed.current = saved;
    if (latest.current.text === previous) {
      latest.current.text = saved;
      setText(saved);
      setDraft(null);
    }
  }, [saved, setDraft]);

  const commit = useCallback(async (): Promise<boolean> => {
    if (inFlight.current) {
      const pendingValue = writingValue.current;
      const result = await completed.current;
      return latest.current.text === pendingValue ? Boolean(result) : commit();
    }
    const value = latest.current.text;
    if (value === confirmed.current) {
      if (mounted.current) {
        setError(null);
        setDraft(null);
      }
      return true;
    }
    const problem = latest.current.validate?.(value) ?? null;
    if (problem) {
      if (mounted.current) {
        setError(problem);
        setDraft(value);
      }
      return false;
    }
    inFlight.current = true;
    writingValue.current = value;
    let success = false;
    let finish!: (result: boolean) => void;
    completed.current = new Promise<boolean>((resolve) => {
      finish = resolve;
    });
    if (mounted.current) setSaving(true);
    try {
      await latest.current.save(value);
      confirmed.current = value;
      success = true;
      if (mounted.current && latest.current.text === value) {
        setError(null);
        setDraft(null);
      }
      return true;
    } catch (failure) {
      if (mounted.current && latest.current.text === value) {
        setError(saveErrorMessage(failure));
        setDraft(value);
      }
      return false;
    } finally {
      inFlight.current = false;
      if (mounted.current) setSaving(false);
      finish(success);
    }
  }, [setDraft]);
  const latestCommit = useRef(commit);
  latestCommit.current = commit;

  useEffect(() => {
    mounted.current = true;
    return () => {
      void latestCommit.current();
      mounted.current = false;
    };
  }, []);

  return {
    text,
    error,
    saving,
    saveNow: commit,
    inputProps: {
      value: text,
      onChange: (event) => {
        const next = event.target.value;
        latest.current.text = next;
        setText(next);
        // Keep the edit available if a close-time save fails or the frame moves.
        setDraft(next === confirmed.current ? null : next);
        if (error && !validate?.(next)) setError(null);
      },
      onBlur: () => void commit(),
      onKeyDown: (event) => {
        if (
          event.key === "Enter" &&
          !multiline &&
          !event.nativeEvent.isComposing
        ) {
          event.preventDefault();
          void commit();
        } else if (event.key === "Escape" && text !== confirmed.current) {
          event.preventDefault();
          event.stopPropagation();
          latest.current.text = confirmed.current;
          setText(confirmed.current);
          setError(null);
          setDraft(null);
        }
      },
      "aria-invalid": error !== null,
    },
  };
}
